/**
 * Nocturne Ciel page (templates/page.nocturne-ciel.json): live demos of the coming subscription, computed
 * in the browser from NocturneGuide for a town.
 *  - <nocturne-ciel-try>: "try it with your town" field (French towns from geo.api.gouv.fr, public, no
 *    key); the town is kept in this browser and sent to every demo on the page as an nx:place event.
 *  - <nocturne-ciel-card data-kind="tonight|alerts|scope">: tonight's list with where to look, the next
 *    alerts as lock-screen notifications, and what a given aperture shows tonight.
 *  - <nocturne-ciel-teaser data-aperture [data-lead]>: on a telescope's page (blocks/nx-product-ciel.liquid),
 *    what that telescope can show tonight from the visitor's town (or Paris); also the home page's Ciel band.
 */
(() => {
  if (customElements.get('nocturne-ciel-card') || !window.NocturneGuide) return;
  const G = window.NocturneGuide;
  const DAY = 86400000;
  const GEO_API = 'https://geo.api.gouv.fr/communes?fields=nom,centre,codesPostaux,departement&limit=1';
  const longDate = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long' });

  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  function countdown(t, now) {
    const d = Math.round((new Date(t).setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / DAY);
    if (d <= 0) return 'aujourd’hui';
    if (d === 1) return 'demain';
    return `dans ${d} jours`;
  }

  class NocturneCielTry extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input');
      this.status = this.querySelector('[data-status]');
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.find(this.input.value.trim());
      });
      // A town tried on an earlier visit: show it straight away.
      const saved = G.loadPlace();
      if (!saved.isDefault) setTimeout(() => this.use(saved), 0);
    }

    async find(v) {
      if (!v) return;
      this.status.textContent = 'Calcul de votre ciel…';
      const url = /^\d{5}$/.test(v) ? `${GEO_API}&codePostal=${v}` : `${GEO_API}&nom=${encodeURIComponent(v)}&boost=population`;
      try {
        const c = (await (await fetch(url)).json()).find((x) => x.centre);
        if (!c) {
          this.status.textContent = 'Aucune commune trouvée : essayez le code postal.';
          return;
        }
        const place = { name: c.nom, sub: c.departement?.nom || '', lat: c.centre.coordinates[1], lon: c.centre.coordinates[0] };
        G.savePlace(place);
        this.use(place);
      } catch (e) {
        this.status.textContent = 'Recherche indisponible pour le moment, réessayez dans un instant.';
      }
    }

    use(place) {
      this.input.value = place.name;
      this.status.textContent = `Ciel calculé en direct pour ${place.name}${place.sub ? ` (${place.sub})` : ''}.`;
      document.dispatchEvent(new CustomEvent('nx:place', { detail: place }));
    }
  }

  class NocturneCielCard extends HTMLElement {
    connectedCallback() {
      // Starts small, so the difference a bigger telescope makes shows at the first click.
      this.aperture = 60;
      this.onPlace = (event) => this.render(event.detail);
      document.addEventListener('nx:place', this.onPlace);
      const saved = G.loadPlace();
      this.render(saved.isDefault ? { name: this.dataset.city || saved.name, lat: parseFloat(this.dataset.lat) || saved.lat, lon: parseFloat(this.dataset.lon) || saved.lon } : saved);
    }

    disconnectedCallback() {
      document.removeEventListener('nx:place', this.onPlace);
    }

    render(place) {
      this.place = place;
      const now = Date.now();
      const body = this.querySelector('[data-body]');
      body.replaceChildren();
      this.querySelectorAll('[data-city]').forEach((n) => (n.textContent = place.name));
      if (this.dataset.kind === 'tonight') this.tonight(body, place, now);
      else if (this.dataset.kind === 'alerts') this.alerts(body, place, now);
      else this.scope(body, place, now);
    }

    tonight(body, place, now) {
      const { items, star } = G.night(place, 0, now);
      const list = el('ul', 'nx-cc__list');
      for (const item of [star, ...items.filter((i) => i !== star)].filter(Boolean).slice(0, 4)) {
        const li = el('li');
        const dot = el('i');
        dot.style.setProperty('--dot', item.color || (item.kind === 'moon' ? '#f5f5f7' : '#a5a3ff'));
        const text = el('p');
        text.append(el('b', '', item.name), el('span', '', cap(item.where)));
        li.append(dot, text, el('time', '', G.fmt(item.at.t)));
        list.append(li);
      }
      body.append(list);
    }

    alerts(body, place, now) {
      body.append(el('p', 'nx-cc__date', cap(longDate.format(now).replace(/ 1 /, ' 1er '))), el('p', 'nx-cc__clock', '21:00'));
      const stack = el('ul', 'nx-cc__notes');
      for (const ev of G.events(place, now).slice(0, 3)) {
        const li = el('li');
        const head = el('p', 'nx-cc__note-head');
        head.append(el('span', 'nx-cc__note-icon', '◐'), el('b', '', 'Nocturne Ciel'), el('small', '', countdown(ev.t, now)));
        li.append(head, el('p', 'nx-cc__note-title', ev.title), el('p', 'nx-cc__note-text', ev.lead));
        stack.append(li);
      }
      body.append(stack);
    }

    scope(body, place, now) {
      const { items } = G.night(place, 0, now);
      const picker = el('div', 'nx-cc__picker');
      picker.setAttribute('role', 'group');
      picker.setAttribute('aria-label', 'Diamètre de votre télescope');
      for (const mm of [60, 90, 130]) {
        const b = el('button', '', `${mm} mm`);
        b.type = 'button';
        b.setAttribute('aria-pressed', String(mm === this.aperture));
        b.addEventListener('click', () => {
          this.aperture = mm;
          this.render(this.place);
        });
        picker.append(b);
      }
      const ok = items.filter((i) => this.aperture >= i.minAp).length;
      const list = el('ul', 'nx-cc__scope');
      for (const item of items.slice(0, 6)) {
        const yes = this.aperture >= item.minAp;
        const li = el('li', yes ? 'is-ok' : 'is-off');
        li.append(el('span', 'nx-cc__mark', yes ? '✓' : '–'), el('b', '', item.name), el('span', '', yes ? 'Visible ce soir' : `À partir de ${item.minAp} mm`));
        list.append(li);
      }
      body.append(picker, el('p', 'nx-cc__count', `${ok} astre${ok > 1 ? 's' : ''} à votre portée ce soir sur ${items.length}`), list);
    }
  }

  class NocturneCielTeaser extends HTMLElement {
    connectedCallback() {
      const aperture = parseFloat(this.dataset.aperture);
      const place = G.loadPlace();
      const items = G.night(place, 0, Date.now()).items.filter((i) => aperture >= i.minAp).slice(0, 5);
      if (!items.length) return;
      const lead = this.dataset.lead || 'Ce soir depuis {place}, ce télescope peut vous montrer :';
      this.querySelector('[data-lead]').textContent = lead.replace('{place}', place.name);
      const chips = this.querySelector('[data-chips]');
      for (const item of items) {
        const chip = el('span');
        const dot = el('i');
        dot.style.setProperty('--dot', item.color || (item.kind === 'moon' ? '#f5f5f7' : '#a5a3ff'));
        chip.append(dot, item.name);
        chips.append(chip);
      }
      this.hidden = false;
    }
  }

  customElements.define('nocturne-ciel-try', NocturneCielTry);
  customElements.define('nocturne-ciel-card', NocturneCielCard);
  customElements.define('nocturne-ciel-teaser', NocturneCielTeaser);
})();
